import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE } from './chartBase';
import { COLOR, FUENTE, UMBRALES } from './tokens';
import PeriodoUnicoKpi from './PeriodoUnicoKpi';
import InfoGrafico from './InfoGrafico';
import { MOCK_CONVERSION_MENSUAL } from './mockDataEjemplos';

const NOMBRES_MES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Tendencia mensual de conversión / eficiencia: qué porcentaje de los turnos de
 * recepción terminó en la apertura de un trámite, mes a mes. Mismo criterio de
 * conversión que TramitesVolumenConversion, pero agregado en el tiempo para ver si
 * la eficiencia de recepción mejora o empeora.
 * @param {{data: Array<{anio: number, mes: number, totalTurnos: number, conTramite: number, tasaConversion: number}>}} props
 */
const ConversionMensualChart = forwardRef(({ data, compacto = false }, ref) => {
    const filas = useMemo(() => (data || []).map((m) => ({
        ...m,
        label: `${NOMBRES_MES[m.mes - 1]} ${m.anio}`,
    })), [data]);

    const { tendenciaPts, promedio } = useMemo(() => {
        if (filas.length === 0) return { tendenciaPts: null, promedio: null };
        // Ponderado por turnos, como la conversión global del encabezado: un promedio simple de los % de
        // cada mes pesaría igual un mes de 20 turnos que uno de 200 y no coincidiría con la tarjeta.
        const baseTotal = filas.reduce((acc, f) => acc + (f.totalTurnos || 0), 0);
        const conTramiteTotal = filas.reduce((acc, f) => acc + (f.conTramite || 0), 0);
        const prom = baseTotal > 0
            ? (conTramiteTotal / baseTotal) * 100
            : filas.reduce((acc, f) => acc + Number(f.tasaConversion || 0), 0) / filas.length;
        if (filas.length < 2) return { tendenciaPts: null, promedio: prom };
        const pts = Number(filas[filas.length - 1].tasaConversion || 0) - Number(filas[0].tasaConversion || 0);
        return { tendenciaPts: pts, promedio: prom };
    }, [filas]);

    const chartData = {
        labels: filas.map((f) => f.label),
        datasets: [{
            label: 'Tasa de conversión',
            data: filas.map((f) => Number(f.tasaConversion || 0)),
            borderColor: COLOR.azul,
            backgroundColor: COLOR.azul,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            tension: 0,
        }],
    };

    const options = {
        ...BASE_OPTIONS,
        plugins: {
            ...BASE_OPTIONS.plugins,
            legend: { display: false },
            tooltip: {
                ...BASE_OPTIONS.plugins.tooltip,
                callbacks: {
                    label: (ctx) => {
                        const fila = filas[ctx.dataIndex];
                        return `${Number(ctx.parsed.y).toFixed(1)}% (${fila.conTramite} de ${fila.totalTurnos} turnos)`;
                    },
                },
            },
        },
        scales: {
            x: { ...EJE_X_BASE },
            y: {
                ...EJE_Y_BASE,
                min: 0,
                ticks: { ...EJE_Y_BASE.ticks, callback: (v) => `${v}%` },
            },
        },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Tendencia de conversión"
                        queMuestra="Te dice si la eficiencia de recepción (qué proporción de turnos termina abriendo un trámite) va mejorando o empeorando mes a mes."
                        ejemploVisual={<ConversionMensualChart compacto data={MOCK_CONVERSION_MENSUAL} />}
                        comoLeerlo={[
                            'Con menos de 2 meses de datos se muestra como una cifra simple, coloreada igual que el promedio (ver abajo).',
                            'Con 2 o más meses, cada punto es la tasa de conversión de ese mes — un solo eje, en %.',
                            `"Conversión promedio" se colorea igual que el resto de la pantalla: verde ≥${UMBRALES.conversionPct.bueno}%, ámbar entre ${UMBRALES.conversionPct.alerta}% y ${UMBRALES.conversionPct.bueno}%, rojo por debajo de ${UMBRALES.conversionPct.alerta}%.`,
                            '"Variación en el período" compara el último mes del rango contra el primero, en puntos porcentuales (verde = subió, rojo = bajó).',
                        ]}
                        analisis="La conversión sube de 30.0% en junio a 42.9% en agosto (+12.9 puntos) — una mejora real y sostenida, no un salto de un solo mes. Aun así, el promedio del período (36.5%) todavía cae en ámbar: la tendencia va en la dirección correcta, pero recién en agosto se cruzó la meta de 40%. Vale la pena identificar qué cambió entre junio y agosto para sostenerlo, en vez de asumir que ya está resuelto."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Tendencia de conversión</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Porcentaje de turnos que terminaron en la apertura de un trámite, mes a mes.
                    </Typography>
                </>
            )}
            {filas.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay datos suficientes para graficar la tendencia.
                </Typography>
            ) : filas.length < 2 ? (
                <PeriodoUnicoKpi items={[{
                    etiqueta: 'Conversión del mes', valor: `${promedio.toFixed(1)}%`,
                    color: promedio >= UMBRALES.conversionPct.bueno ? COLOR.verde : promedio >= UMBRALES.conversionPct.alerta ? COLOR.ambar : COLOR.rojo,
                }]} />
            ) : (
                <>
                    <Box sx={{ height: compacto ? 150 : 220 }}>
                        <Line ref={ref} data={chartData} options={options} />
                    </Box>
                    {!compacto && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', marginTop: '18px' }}>
                            <Resumen etiqueta="Conversión promedio" valor={`${promedio.toFixed(1)}%`}
                                color={promedio >= UMBRALES.conversionPct.bueno ? COLOR.verde : promedio >= UMBRALES.conversionPct.alerta ? COLOR.ambar : COLOR.rojo} />
                            <Resumen etiqueta="Variación en el período"
                                valor={tendenciaPts !== null ? `${tendenciaPts > 0 ? '+' : ''}${tendenciaPts.toFixed(1)} pts` : '—'}
                                color={tendenciaPts === null ? COLOR.negro : tendenciaPts >= 0 ? COLOR.verde : COLOR.rojo} />
                        </Box>
                    )}
                </>
            )}
        </Card>
    );
});

const Resumen = ({ etiqueta, valor, color }) => (
    <Box sx={{ background: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: '8px', padding: '10px 12px' }}>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: COLOR.grisTexto }}>
            {etiqueta}
        </Typography>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 18, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
            {valor}
        </Typography>
    </Box>
);

export default ConversionMensualChart;
